<template>
  <div class="loinc-intro-page">
    <Navbar />
    <PageBanner :title="pageTitle" />

    <Breadcrumb :items="breadcrumbItems" class="intro-breadcrumb" />

    <div v-if="isLoading" class="edu-layout">
      <main class="edu-content">
        <section class="edu-section">
          <p class="edu-section-text">{{ t("common.loading") }}</p>
        </section>
      </main>
    </div>

    <div v-else-if="errorMessage" class="edu-layout">
      <main class="edu-content">
        <section class="edu-section">
          <p class="edu-section-text">{{ errorMessage }}</p>
        </section>
      </main>
    </div>

    <div v-else class="edu-layout">
      <!-- Desktop left sidebar (sticky) -->
      <aside class="edu-sidebar">
        <nav :aria-label="t('common.tocLabel')">
          <button
            v-for="section in sections"
            :key="section.id"
            class="edu-sidebar-link"
            :class="{ 'is-active': activeId === section.id }"
            @click="scrollToSection(section.id)"
          >
            {{ section.label }}
          </button>
        </nav>
      </aside>

      <!-- Mobile sticky TOC -->
      <div
        class="mobile-toc"
        role="navigation"
        :aria-label="t('common.tocLabel')"
      >
        <button
          class="toc-header"
          @click="toggleExpand"
          :aria-expanded="isExpanded"
        >
          <span class="toc-header-title">{{ t("common.tocLabel") }}</span>
          <svg
            class="toc-chevron"
            :class="{ 'is-open': isExpanded }"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="#0068A8"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div class="toc-body">
          <template v-if="isExpanded">
            <button
              v-for="section in sections"
              :key="section.id"
              class="toc-link"
              :class="{ 'is-active': activeId === section.id }"
              @click="collapseAndScroll(section.id)"
            >
              {{ section.label }}
            </button>
          </template>
          <template v-else>
            <button
              class="toc-link toc-current"
              @click="scrollToSection(activeId)"
            >
              {{ sections.find((s) => s.id === activeId)?.label }}
            </button>
          </template>
        </div>
      </div>

      <!-- Main content -->
      <main class="edu-content">
        <div v-for="(item, index) in localizedItems" :key="item.id">
          <section
            :id="item.elementId"
            class="edu-section"
            :class="{ 'edu-section--bg': index % 2 === 1 }"
          >
            <h2 class="edu-section-title">{{ item.localizedTitle }}</h2>
            <div
              class="rich-html"
              v-html="item.localizedContent"
              @click="handleBodyClick"
            ></div>
          </section>
          <hr v-if="index < localizedItems.length - 1" class="edu-divider" />
        </div>
      </main>

      <!-- Lightbox -->
      <ImageLightbox
        :visible="showLightbox"
        :src="currentLightboxImage"
        :alt="pageTitle"
        @close="closeLightbox"
      />
    </div>

    <FooterSection />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import { contentAPI } from "@/services/api";
import { useEducationToc } from "@/composables/useEducationToc";

const { locale, t } = useI18n();

const isLoading = ref(true);
const errorMessage = ref("");
const apiItems = ref([]);

const parseImageCaptions = (html) => {
  if (!html) return "";
  return html.replace(/<img([^>]+)>/gi, (imgTag, attrs) => {
    const altMatch = attrs.match(/alt=["']([^"']*)["']/i);
    const altText = altMatch ? altMatch[1].trim() : "";
    if (altText) {
      return `<figure class="rich-figure"><img${attrs}><figcaption class="image-caption">${altText}</figcaption></figure>`;
    }
    return imgTag;
  });
};

const localizedItems = computed(() =>
  apiItems.value.map((item, index) => {
    const title = locale.value === "en-US" ? (item.title_en || item.title) : item.title;
    const content = locale.value === "en-US" ? (item.content_en || item.content) : item.content;
    return {
      ...item,
      localizedTitle: title,
      localizedContent: parseImageCaptions(content),
      elementId: `section-${index}`,
    };
  })
);

const pageTitle = computed(() => {
  const first = localizedItems.value[0];
  return first?.localizedTitle || t("snomedCT.title");
});

const sections = computed(() =>
  localizedItems.value.map((item) => ({
    id: item.elementId,
    label: item.localizedTitle,
  }))
);

const SECTION_IDS = computed(() => sections.value.map((s) => s.id));

const {
  activeId,
  isExpanded,
  scrollToSection,
  toggleExpand,
  collapseAndScroll,
} = useEducationToc(SECTION_IDS);

const breadcrumbItems = computed(() => [
  { label: t("common.home"), path: "/" },
  { label: t("navbar.snomedCtIntro"), path: "/rxnorm/intro" },
  { label: t("navbar.whatIsSnomedCt") },
]);

async function fetchWhatIs() {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    const res = await contentAPI.standardIntro({ section: "what_is" });
    apiItems.value = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || t("common.systemError");
  } finally {
    isLoading.value = false;
  }
}

import { useLightbox } from "@/composables/useLightbox";
import ImageLightbox from "@/components/ImageLightbox.vue";

const { showLightbox, currentLightboxImage, closeLightbox, handleBodyClick } =
  useLightbox();

onMounted(() => {
  fetchWhatIs();
});
</script>

<style lang="scss">
@use "@/styles/rich-content.scss" as rc;

$nav-h: 58px;
$sidebar-w: 260px;
$toc-bg: #dcf3f9;
$toc-link: #0068a8;
$active-color: #0079bb;
$section-alt-bg: #f9f9f9;
$title-color: #004a89;
$text-color: #333333;

.loinc-intro-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.edu-layout {
  width: 100%;
  max-width: 1200px;
  margin: 50px auto 80px;
  display: flex;
  align-items: flex-start;
  position: relative;
  padding: 0 20px;
}

.edu-sidebar {
  width: $sidebar-w;
  flex: 0 0 $sidebar-w;
  border-right: 1px solid #ededed;
  position: sticky;
  top: calc(#{$nav-h} + 24px);
  align-self: flex-start;
  padding-right: 0;
  max-height: calc(100vh - #{$nav-h} - 48px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cccccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999999;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 32px 0 16px;
  }
}

.edu-sidebar-link {
  display: inline-flex;
  width: fit-content;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding-bottom: 8px;
  cursor: pointer;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 32px;
  color: #696969;
  text-align: left;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: $active-color;
  }

  &.is-active {
    color: $active-color;
    border-bottom-color: $active-color;
  }
}

.mobile-toc {
  display: none;
}

.edu-content {
  flex: 1;
  min-width: 0;
}

.edu-section {
  padding: 48px 64px;
  max-width: 840px;

  &--bg {
    background: transparent;
    max-width: 100%;
    padding-inline: 64px;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: min(0px, calc(600px - 50vw));
      background: $section-alt-bg;
      z-index: 0;
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }
}

.edu-divider {
  border: none;
  border-top: 1px solid #ededed;
  margin: 0;
}

.edu-section-title {
  margin: 0 0 28px;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 44px;
  letter-spacing: 0.02em;
  color: $title-color;
}

.edu-section-text {
  margin: 0 0 20px;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 36px;
  color: $text-color;

  &:last-child {
    margin-bottom: 0;
  }
}

:deep(.rich-html) {
  @include rc.rich-content;

  p {
    font-size: 16px;
    color: #666666;
    margin-bottom: 24px;
    text-align: justify;

    @media (max-width: 768px) {
      font-size: 15px;
      margin-bottom: 20px;
    }
  }

  img {
    width: 100%;
    height: auto;
    max-width: 900px;
    display: block;
    margin: 16px auto 0;
    cursor: zoom-in;
  }
}

@media (max-width: 960px) {
  .edu-layout {
    flex-direction: column;
    margin: 0 0 40px;
    max-width: 100%;
    padding: 0;
  }

  .edu-sidebar {
    display: none;
  }

  .mobile-toc {
    display: block;
    width: 100%;
    position: sticky;
    top: $nav-h;
    z-index: 200;
    background: $toc-bg;
    border-bottom: 1px solid #b5dff0;
  }

  .toc-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    background: none;
    border: none;
    cursor: pointer;
    gap: 8px;
  }

  .toc-header-title {
    font-family: "Noto Sans TC", sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: $toc-link;
  }

  .toc-chevron {
    flex-shrink: 0;
    transition: transform 0.25s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  .toc-body {
    display: flex;
    flex-direction: column;
  }

  .toc-link {
    display: block;
    width: 100%;
    padding: 10px 20px 10px 28px;
    background: none;
    border: none;
    border-left: 3px solid transparent;
    cursor: pointer;
    text-align: left;
    font-family: "Noto Sans TC", sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: $toc-link;
    transition: border-color 0.2s, background 0.2s;

    &.is-active,
    &.toc-current {
      border-left-color: $toc-link;
      background: rgba(0, 104, 168, 0.06);
    }

    &:hover {
      background: rgba(0, 104, 168, 0.1);
    }
  }

  .edu-section {
    padding: 36px 20px;
    max-width: 100%;

    &--bg {
      padding-inline: 20px;
    }
  }

  .edu-section-title {
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 20px;
  }

  .edu-section-text {
    font-size: 16px;
    line-height: 28px;
  }
}

@media (max-width: 400px) {
  .toc-header-title {
    font-size: 14px;
  }

  .edu-section-title {
    font-size: 22px;
  }
}
</style>
